import React, { useEffect, useState } from 'react';
import { BookOpen, Save, Send } from 'lucide-react';
import type { HomeAnnouncementConfig } from '../types';
import {
  fetchHomeAnnouncementConfig,
  sanitizeHomeAnnouncementConfig,
  saveHomeAnnouncementConfig
} from '../storage';

const HOME_ANNOUNCEMENT_VERSION_KEY = 'kendama-home-announcement-version';
const HOME_ANNOUNCEMENT_UPDATED_EVENT = 'kendama-home-announcement-updated';

const AdminAnnouncement: React.FC = () => {
  const [config, setConfig] = useState<HomeAnnouncementConfig>(() => sanitizeHomeAnnouncementConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        setLoading(true);
        setMessage('');
        const remoteConfig = await fetchHomeAnnouncementConfig();
        if (!mounted) return;
        setConfig(sanitizeHomeAnnouncementConfig(remoteConfig));
      } catch (error) {
        if (!mounted) return;
        setMessage((error as Error).message || '加载首页公告失败');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadConfig();

    return () => {
      mounted = false;
    };
  }, []);

  const syncAnnouncementUpdate = (nextConfig: HomeAnnouncementConfig) => {
    window.localStorage.setItem(
      HOME_ANNOUNCEMENT_VERSION_KEY,
      String(Math.max(nextConfig.updatedAt, nextConfig.pushedAt))
    );
    window.dispatchEvent(new Event(HOME_ANNOUNCEMENT_UPDATED_EVENT));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      const savedConfig = await saveHomeAnnouncementConfig({
        title: config.title,
        content: config.content,
        updatedAt: Date.now(),
        pushedAt: config.pushedAt
      });
      setConfig(savedConfig);
      syncAnnouncementUpdate(savedConfig);
      setMessage(savedConfig.title.trim() || savedConfig.content.trim() ? '首页公告已保存。' : '首页公告已清空，玩家端将显示暂无公告。');
    } catch (error) {
      setMessage((error as Error).message || '保存首页公告失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center font-orbitron text-zinc-500 animate-pulse">LOADING ANNOUNCEMENT...</div>;
  }

  const handlePush = async () => {
    if (!hasContent) {
      setMessage('请先设置公告标题或内容，再执行推送。');
      return;
    }

    try {
      setPushing(true);
      setMessage('');
      const pushedConfig = await saveHomeAnnouncementConfig({
        title: config.title,
        content: config.content,
        updatedAt: Date.now(),
        pushedAt: Date.now()
      });
      setConfig(pushedConfig);
      syncAnnouncementUpdate(pushedConfig);
      setMessage('首页公告已推送。所有用户下次进入首页时会自动弹出一次。');
    } catch (error) {
      setMessage((error as Error).message || '推送首页公告失败');
    } finally {
      setPushing(false);
    }
  };

  const hasContent = config.title.trim().length > 0 || config.content.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-500/25 bg-black/45 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-orbitron text-sm uppercase tracking-[0.18em] text-amber-200">
              <BookOpen size={16} />
              首页公告
            </div>
            <div className="mt-2 max-w-2xl text-sm leading-6 text-zinc-300">
              保存只会更新公告内容。点击推送后，所有用户会在下次进入首页时自动弹出一次；每位用户每次推送仅弹出一次。
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || pushing}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/15 px-4 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-amber-100 transition-colors hover:border-amber-300/50 hover:bg-amber-400/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? '保存中...' : '保存公告'}
            </button>
            <button
              type="button"
              onClick={() => void handlePush()}
              disabled={saving || pushing || !hasContent}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send size={14} />
              {pushing ? '推送中...' : '推送公告'}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">公告标题</div>
            <input
              value={config.title}
              onChange={(event) => {
                setConfig(previous => ({
                  ...previous,
                  title: event.target.value
                }));
                setMessage('标题已修改，保存后生效。');
              }}
              placeholder="请输入首页公告标题..."
              className="mt-3 w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-amber-400/50"
            />
            <div className="mt-4 text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">公告内容</div>
            <textarea
              value={config.content}
              onChange={(event) => {
                setConfig(previous => ({
                  ...previous,
                  content: event.target.value
                }));
                setMessage('内容已修改，保存后生效。');
              }}
              placeholder="请输入首页公告内容..."
              className="mt-3 min-h-[220px] w-full resize-y rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-amber-400/50"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">当前状态</div>
            <div className={`mt-3 inline-flex rounded-full border px-3 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] ${
              hasContent
                ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
                : 'border-zinc-700 bg-zinc-900/70 text-zinc-400'
            }`}>
              {hasContent ? '已设置公告' : '暂无公告'}
            </div>
            <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-black/30 p-4">
              <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">预览</div>
              <div className="mt-3 text-base font-orbitron text-white">
                {config.title.trim() || '首页公告'}
              </div>
              <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-200">
                {config.content.trim() || '暂无公告'}
              </div>
            </div>
            <div className="mt-4 text-xs leading-6 text-zinc-500">
              最近更新时间：{config.updatedAt > 0 ? new Date(config.updatedAt).toLocaleString() : '--'}
            </div>
            <div className="mt-1 text-xs leading-6 text-zinc-500">
              最近推送时间：{config.pushedAt > 0 ? new Date(config.pushedAt).toLocaleString() : '--'}
            </div>
          </div>
        </div>

        {message && (
          <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncement;
